import { LitElement, html, css, PropertyValues } from 'lit'
import { property, customElement } from 'lit/decorators.js'
import {
    LegSpeedSettings,
    VesselPlanningModel,
} from '../../../src/models/vessel-planning-model'
import '../../voyage-popup/src/voyage-popup'
import { VesselPerformanceModel } from '../../../src/models/vessel-performance-model'
import { getDefaultCbForIMO } from './vessel-default-cb-values'
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style'
import '@maersk-global/mds-components-core/mc-radio'
import '@maersk-global/mds-components-core/mc-number-stepper'
import { VoyageImoModel } from '../../../src/models/voyage-imo-model'
import { isOnline } from '../../../src/utils/helpers';

const MpsToKnots = (60 * 60) / 1852
const isVesselOnCloud = (<any>window)['appenv'].vesselOnCloud

@customElement('voy-vessel-chars-popup-cloud')
export class VoyVesselCharsPopupCloud extends LitElement {
    @property({ type: Boolean }) shown: boolean = false

    static get styles() {
        return [
            voyageCursorStyles,
            css`
                .popup-container {
                    margin: 24px 24px 14px 24px;
                }

                h1 {
                    font-family: 'Maersk Text';
                    font-size: 18px;
                    font-style: normal;
                    font-weight: 700;
                    line-height: 24px;
                    color: var(--neutrals-grey-02, black);
                    margin: 0 0 8px 0;
                }

                p,
                span {
                    margin: 0;
                    color: var(--neutrals-grey-02, black);
                    font-family: MaerskText;
                    font-size: 13px;
                }

                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-auto-rows: minmax(min-content, auto);
                    gap: 10px 32px;
                    margin-top: 20px;
                    margin-bottom: 14px;
                }

                @media (max-width: 768px) {
                    .form-grid {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                }

                mc-number-stepper::part(label) {
                    font-size: 14px;
                }

                /** Override bug on MDS where the right border is not visible */
                mc-number-stepper[invalid]::part(field) {
                    border-right-width: 2px !important;
                }
                    
                .button-group-container {
                    display: flex;
                    flex-direction: row;
                    gap: 10px;
                    align-items: center;
                    margin-bottom: 8px;
                }
            `,
        ]
    }

    render() {
        const enableAcceptButton =
            this._height >= 0 &&
            this._minSpeed >= 0 &&
            this._maxSpeed >= 0 &&
            this._blockCoefficient >= 0 &&
            this._blockCoefficient <= 1 &&
            this._beamSize >= 0

        return html`
            <voyage-popup
                accept-button-text="Save and continue"
                .shown=${this.shown}
                @accept=${this._save}
                @close=${() => this.dispatchEvent(new Event("close"))}
                .disableAcceptButton=${!enableAcceptButton}
                .rejectButtonText=${this._allowCancel ? 'Cancel' : ''}
                .noHeader=${true}
                .noCloseButton=${!this._allowCancel}
            >
                <div class="popup-container">
                    <h1>Vessel Characteristics</h1>
                    <p>
                        Please enter the below details and speed values are only
                        used for passage plans, not for calculations (for now).
                    </p>

                    <!-- 2 x 3 Grid (2 cols, 3 rows) of number stepper inputs -->
                    <div class="form-grid">
                        <mc-number-stepper
                            name="height"
                            label="Overall height (meters)"
                            placeholder="Enter value"
                            min="0"
                            step="0.01"
                            value=${`${this._height >= 0 ? this._height : ''}`}
                            @input=${(e: InputEvent) => {
                                this._height = parseFloat(
                                    (e.target as HTMLInputElement).value,
                                )
                                console.log(this._height)
                            }}
                            ?invalid=${this._height < 0 ||
                            isNaN(this._height) ||
                            !this._height}
                        >
                        </mc-number-stepper>
                        <mc-number-stepper
                            name="minSpeed"
                            label="Minimum speed (knots)"
                            placeholder="Enter value"
                            min="0"
                            step="0.01"
                            value=${`${
                                this._minSpeed >= 0 ? this._minSpeed : ''
                            }`}
                            @input=${(e: InputEvent) =>
                                (this._minSpeed = parseFloat(
                                    (e.target as HTMLInputElement).value,
                                ))}
                            ?invalid=${this._minSpeed < 0 ||
                            isNaN(this._minSpeed) ||
                            !this._minSpeed}
                        >
                        </mc-number-stepper>
                        <mc-number-stepper
                            name="maxSpeed"
                            label="Maximum speed (knots)"
                            placeholder="Enter value"
                            min="0"
                            step="0.01"
                            value=${`${
                                this._maxSpeed >= 0 ? this._maxSpeed : ''
                            }`}
                            @input=${(e: InputEvent) =>
                                (this._maxSpeed = parseFloat(
                                    (e.target as HTMLInputElement).value,
                                ))}
                            ?invalid=${this._maxSpeed < 0 ||
                            isNaN(this._maxSpeed) ||
                            !this._maxSpeed}
                        >
                        </mc-number-stepper>
                        <mc-number-stepper
                            name="blockCoefficient"
                            label="Block Coefficient"
                            placeholder="Enter value"
                            min="0"
                            max="1"
                            step="0.01"
                            value=${`${
                                this._blockCoefficient >= 0
                                    ? this._blockCoefficient
                                    : ''
                            }`}
                            @input=${(e: InputEvent) =>
                                (this._blockCoefficient = parseFloat(
                                    (e.target as HTMLInputElement).value,
                                ))}
                            ?invalid=${this._blockCoefficient < 0 ||
                            this._blockCoefficient > 1 ||
                            isNaN(this._blockCoefficient) ||
                            !this._blockCoefficient}
                        >
                        </mc-number-stepper>
                        <mc-number-stepper
                            name="beamSize"
                            label="Beam size (meters)"
                            placeholder="Enter value"
                            min="0"
                            step="0.01"
                            value=${`${
                                this._beamSize >= 0 ? this._beamSize : ''
                            }`}
                            @input=${(e: InputEvent) =>
                                (this._beamSize = parseFloat(
                                    (e.target as HTMLInputElement).value,
                                ))}
                            ?invalid=${this._beamSize < 0 ||
                            isNaN(this._beamSize) ||
                            !this._beamSize}
                        >
                        </mc-number-stepper>
                    </div>
                    <div class="button-group-container">
                        <span
                            >Select the Passage Plan - WP number start at:</span
                        >
                        <mc-button-group selectiontype="single" fit="small">
                            <mc-button-group-item
                                label="0"
                                @click=${() => (this._ecdisWPStartIndex = 0)}
                                ?selected=${this._ecdisWPStartIndex === 0}
                            >
                                0
                            </mc-button-group-item>
                            <mc-button-group-item
                                label="1"
                                @click=${() => (this._ecdisWPStartIndex = 1)}
                                ?selected=${this._ecdisWPStartIndex === 1}
                            >
                                1
                            </mc-button-group-item>
                        </mc-button-group>
                    </div>
                    <div class="button-group-container">
                        <span>Select the Squat calculation based on:</span>
                        <mc-button-group selectiontype="single" fit="small">
                            <mc-button-group-item
                                label="Calcualted leg speed"
                                @click=${() =>
                                    (this._legSpeedSetting =
                                        LegSpeedSettings.CALCULATED_SPEED)}
                                ?selected=${this._legSpeedSetting ===
                                LegSpeedSettings.CALCULATED_SPEED}
                            >
                                Calcualted leg speed
                            </mc-button-group-item>
                            <mc-button-group-item
                                label="Max leg speed"
                                @click=${() =>
                                    (this._legSpeedSetting =
                                        LegSpeedSettings.MAX_SPEED)}
                                ?selected=${this._legSpeedSetting ===
                                LegSpeedSettings.MAX_SPEED}
                            >
                                Max leg speed
                            </mc-button-group-item>
                        </mc-button-group>
                    </div>
                </div>
            </voyage-popup>
        `
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has('shown') && this.shown) {
            this._onShown()
        }
    }
    firstUpdated() {
        this._getVesselsCharacterictics()
    }

    private async _getVesselsCharacterictics() {
        const chars = await VesselPlanningModel!.getVesselCharacteristics()

        if (!chars?.legSpeedSetting || !chars?.beamSize) {
            this.shown = true;
        }
        if (!isOnline()){
            this.shown = false;
        }   
    }

    @property({ attribute: false }) private _height: number = -1
    @property({ attribute: false }) private _minSpeed: number = -1
    @property({ attribute: false }) private _maxSpeed: number = -1
    @property({ attribute: false }) private _blockCoefficient: number = -1
    @property({ attribute: false }) private _beamSize: number = -1
    @property({ attribute: false }) private _ecdisWPStartIndex: number = 1
    @property({ attribute: false }) private _allowCancel: boolean = true
    @property({ attribute: false })
    private _legSpeedSetting: LegSpeedSettings | null = null

    private async _onShown() {
        const charateristics =
            await VesselPlanningModel!.getVesselCharacteristics()
        const imoNo = !isVesselOnCloud
            ? (<any>window)['appenv'].vesselIMONumber
            : VoyageImoModel?.imo
        if (!charateristics) {
            this._height = -1
            this._minSpeed = -1
            this._maxSpeed = -1
            this._blockCoefficient = -1
            this._beamSize = -1
            this._ecdisWPStartIndex = 1
            this._allowCancel = false
            this._legSpeedSetting = LegSpeedSettings.CALCULATED_SPEED
            // Get block coefficient default from our table
            this._blockCoefficient = getDefaultCbForIMO(imoNo)

            // Use PAPIs maxSpeed as default value
            const configData =
                await VesselPerformanceModel!.getConfigurationData()
            if (configData && configData.maxSpeed) {
                this._maxSpeed = configData.maxSpeed
            }
        } else {
            this._height = charateristics.height
            this._maxSpeed = charateristics.maxSpeed * MpsToKnots
            this._minSpeed = charateristics.minSpeed * MpsToKnots
            this._blockCoefficient = charateristics.blockCoefficient
            this._beamSize = charateristics.beamSize
            this._ecdisWPStartIndex = charateristics.ecdisWPStartIndex
            this._allowCancel = true
            this._legSpeedSetting = charateristics.legSpeedSetting ?? null
            // Get block coefficient default from our table
        }
    }

    private _save() {
        VesselPlanningModel!.setVesselCharacteristics({
            height: this._height,
            maxSpeed: this._maxSpeed / MpsToKnots,
            minSpeed: this._minSpeed / MpsToKnots,
            blockCoefficient: this._blockCoefficient,
            beamSize: this._beamSize,
            ecdisWPStartIndex: this._ecdisWPStartIndex,
            legSpeedSetting:
                this._legSpeedSetting ?? LegSpeedSettings.CALCULATED_SPEED, // default to calculated speed which we get from Hydra
        })
        this.shown = false
        this.dispatchEvent(new Event('close'))
    }
}
